import React from 'react';
import { SocialMediaPost, SOCIAL_POST_STATUS_DRAFT, SocialMediaContent } from '../../types';
import {
  FacebookPageDto,
  InstagramAccountDto,
  SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT,
  SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE,
  SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
} from '../../../social-posting/socialPostingApi';
import { useFetchedFacebookAccount } from '../../../facebook/hooks/useFetchedFacebookAccount';
import { useConnectFacebook } from '../../../facebook/hooks/useConnectFacebook';
import { SocialMediaAccountPost } from './SocialMediaAccountPost';
import { useMemoCompare } from '../../../hooks/useMemoCompare';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { useFetchedInstagramAccount } from '../../../instagram/hooks/useFetchedInstagramAccount';
import { DirectInstagramAccount } from '../../../instagram/types';
import { useConnectInstagram } from '../../../instagram/hooks/useConnectInstagram';

export const SocialMediaAccountList = (props: {
  posts: SocialMediaPost[];
  content: SocialMediaContent;
  publishOn: Date;
  onChanged: (posts: SocialMediaPost[]) => void;
}) => {
  const { posts, content, publishOn, onChanged } = props;

  const {
    query: {
      data: facebookAccount,
      isLoading: isFacebookAccountLoading,
      isError: isFacebookAccountLoadingError,
    },
    queryStatusComponent: facebookStatusComponent,
  } = useFetchedFacebookAccount();

  const {
    query: {
      data: instagramAccount,
      isLoading: isInstagramAccountLoading,
      isError: isInstagramAccountLoadingError,
    },
    queryStatusComponent: instagramStatusComponent,
  } = useFetchedInstagramAccount();

  const { startConnect: startConnectFacebook, isConnecting: isFacebookConnecting } =
    useConnectFacebook();
  const { startConnect: startConnectInstagram, isConnecting: isInstagramConnecting } =
    useConnectInstagram();

  const isFacebookConnected = !!facebookAccount && !facebookAccount.isDisconnected;
  const isInstagramConnected = !!instagramAccount && !instagramAccount.isDisconnected;

  const handleConnectFacebookClick = () => {
    startConnectFacebook();
  };

  const handleConnectInstagramClick = () => {
    startConnectInstagram();
  };

  const getMatchedAccounts = () => {
    const matchedAccounts: Array<{
      post: SocialMediaPost | null;
      account: FacebookPageDto | InstagramAccountDto | DirectInstagramAccount | null;
    }> = [];

    if (isFacebookConnected) {
      facebookAccount.facebookPages.forEach((page) => {
        const post = posts.find(
          (post) => post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE && post.socialAssetId === page.id
        );
        matchedAccounts.push({
          post: post ? post : null,
          account: page,
        });
      });

      facebookAccount.instagramAccounts.forEach((account) => {
        const post = posts.find(
          (post) =>
            post.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT && post.socialAssetId === account.id
        );
        matchedAccounts.push({
          post: post ? post : null,
          account: account,
        });
      });
    }

    if (isInstagramConnected) {
      const directInstagramPost = posts.find(
        (post) =>
          post.type === SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT &&
          post.socialAssetId === instagramAccount.id
      );
      matchedAccounts.push({
        post: directInstagramPost ? directInstagramPost : null,
        account: instagramAccount,
      });
    }

    posts.forEach((post) => {
      const pageFound =
        post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE
          ? isFacebookConnected &&
            facebookAccount.facebookPages.some((page) => page.id === post.socialAssetId)
          : post.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT
          ? isFacebookConnected &&
            facebookAccount.instagramAccounts.some((page) => page.id === post.socialAssetId)
          : instagramAccount?.id === post.socialAssetId;
      if (!pageFound) {
        matchedAccounts.push({
          post,
          account: null,
        });
      }
    });

    return matchedAccounts;
  };

  const matchedAccounts = useMemoCompare(getMatchedAccounts(), (a, b) => _.isEqual(a, b));

  const handleSelectionChange = (
    isSelected: boolean,
    account: FacebookPageDto | InstagramAccountDto | DirectInstagramAccount
  ) => {
    const newPosts = [...posts];

    if (isSelected && account.id) {
      const newPost: SocialMediaPost = {
        socialAssetId: account.id,
        status: SOCIAL_POST_STATUS_DRAFT,
        publishing: null,
        type: account.type,
      };
      newPosts.push(newPost);
    } else {
      const index = newPosts.findIndex(
        (post) => post.type === account.type && post.socialAssetId === account.id
      );
      if (index !== -1) {
        newPosts.splice(index, 1);
      }
    }

    onChanged(newPosts);
  };

  if (isFacebookAccountLoading || isInstagramAccountLoading) {
    return <div>Loading social media accounts...</div>;
  }

  if (!isFacebookConnected && !isInstagramConnected) {
    return (
      <div className="social-media-account-list">
        <div className="social-media-accounts-horizontal">
          {isFacebookAccountLoadingError ? (
            facebookStatusComponent
          ) : (
            <Button
              variant="primary"
              className="button-apply"
              onClick={handleConnectFacebookClick}
              disabled={isFacebookConnecting}
            >
              {isFacebookConnecting ? 'Connecting...' : 'Connect Facebook & Instagram'}
            </Button>
          )}
          {isInstagramAccountLoadingError ? (
            instagramStatusComponent
          ) : (
            <Button
              variant="primary"
              className="button-apply"
              onClick={handleConnectInstagramClick}
              disabled={isInstagramConnecting}
            >
              {isInstagramConnecting ? 'Connecting...' : 'Connect Instagram'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="social-media-account-list">
      <div className="social-media-accounts-horizontal">
        {matchedAccounts.map(({ post, account }, index) => (
          <SocialMediaAccountPost
            key={`${account?.type ? account.type : post?.type}-${account?.id}-${index}`}
            post={post}
            account={account}
            content={content}
            publishOn={publishOn}
            readonly={false}
            onSelectionChange={(isSelected) => {
              if (account) handleSelectionChange(isSelected, account);
            }}
          />
        ))}
      </div>
    </div>
  );
};
